import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import '../styles/preloader.css';

// Only assets that paint in the first viewport for the current breakpoint.
// Desktop-only hero hands / peeking head must not block mobile first paint.
const getCriticalAssets = () => {
  if (typeof window === 'undefined') return [];
  const wide = window.matchMedia('(min-width: 1024px)').matches;
  const xwide = window.matchMedia('(min-width: 1280px)').matches;
  const assets = [];
  if (wide) assets.push('/images/hoofd.webp');
  if (xwide) assets.push('/images/hand.webp', '/images/hand2.webp');
  return assets;
};

const REVEAL_AFTER_MS = 150; // don't flash the loader for near-instant (cached) loads
const MIN_VISIBLE_MS = 500; // if it does show, keep it on screen long enough to read
const MAX_WAIT_MS = 6000; // safety net: never block forever on a broken/slow asset

export default function Preloader({ onComplete }) {
  const completedRef = useRef(false);

  useEffect(() => {
    const startTime = Date.now();
    let revealed = false;
    let exited = false;

    // Lock scroll while the loader can be on screen.
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const preloaderEl = document.querySelector('.preloader');
    const progressEl = document.querySelector('.loader-progress');

    // Start hidden so fast (cached) loads never paint the loader at all.
    if (preloaderEl) gsap.set(preloaderEl, { autoAlpha: 0 });

    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      onComplete?.();
    };

    const criticalAssets = getCriticalAssets();
    let loadedCount = 0;
    const total = criticalAssets.length;

    const setProgress = (pct) => {
      if (progressEl) progressEl.textContent = `${Math.min(100, Math.round(pct))}%`;
    };
    setProgress(0);

    const reveal = () => {
      if (revealed || exited || completedRef.current) return;
      revealed = true;
      if (preloaderEl) gsap.to(preloaderEl, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });
    };
    const revealTimer = setTimeout(reveal, REVEAL_AFTER_MS);

    const exit = () => {
      if (exited || completedRef.current) return;
      exited = true;
      clearTimeout(revealTimer);
      setProgress(100);

      // Never painted (instant load) — skip the animation entirely.
      if (!revealed) {
        finish();
        return;
      }

      const tl = gsap.timeline({ onComplete: finish });
      tl.to('.loader', { rotation: '+=180', duration: 0.7, ease: 'power4.inOut' })
        .to('.loader', { scale: 0, duration: 0.5, ease: 'power4.inOut' }, '>-0.05')
        .to(
          '.preloader',
          {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
            duration: 0.8,
            ease: 'power4.inOut',
          },
          '<'
        );
    };

    const safety = setTimeout(exit, MAX_WAIT_MS);

    // Mobile / narrow viewports: nothing critical — release ASAP.
    if (total === 0) {
      clearTimeout(revealTimer);
      clearTimeout(safety);
      setProgress(100);
      finish();
      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    }

    const assetPromises = criticalAssets.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          const done = () => {
            loadedCount += 1;
            setProgress((loadedCount / total) * 100);
            resolve();
          };
          img.onload = done;
          img.onerror = done;
          img.src = src;
          // Already cached/decoded — resolve synchronously on next tick.
          if (img.complete) done();
        })
    );

    Promise.all(assetPromises).then(() => {
      const elapsed = Date.now() - startTime;
      // Only enforce the minimum on-screen time if the loader actually showed.
      const wait = revealed ? Math.max(0, MIN_VISIBLE_MS - elapsed) : 0;
      setTimeout(exit, wait);
    });

    return () => {
      clearTimeout(revealTimer);
      clearTimeout(safety);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [onComplete]);

  return (
    <div className="preloader">
      <div className="loader"></div>
      <div
        className="loader-progress"
        style={{
          position: 'absolute',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#262624',
          fontFamily: 'Space Grotesk, sans-serif',
          fontWeight: 700,
          fontSize: '18px',
          letterSpacing: '-0.4px',
        }}
      >
        0%
      </div>
    </div>
  );
}
